import React from "react";
import { Radio, Database, Sparkles, Wifi } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * LiveDataChip — surfaces data-freshness state without cluttering the UI.
 *
 * Modes:
 *   • Normal user (default):
 *       - cache_hit=true                              → "Cached · 5m ago"
 *       - live_data_status="live", cache_hit=false    → "Live data"
 *       - anything else (mock / fallback)             → render nothing (silent)
 *   • Admin / dev mode (when an admin token is in localStorage):
 *       - Always renders a detailed chip with all four observable fields:
 *           live_data_status, cache_hit, cache_age, data_source (fmp|cache|mock)
 *       - Provides a tap-to-open tooltip with the full payload metadata.
 *
 * The chip is intentionally subtle (small pill, single-line, no exclamation
 * icons) so paid users never see a "something is wrong" signal when we
 * silently fall back to mock. Premium polish first.
 */
function formatRelative(iso) {
  if (!iso) return "just now";
  let date;
  try {
    date = new Date(iso);
  } catch {
    return "just now";
  }
  if (Number.isNaN(date.getTime())) return "just now";
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  return `${day}d ago`;
}

function isAdminMode() {
  if (typeof window === "undefined") return false;
  try {
    // WS-8: admin credential moved to an HttpOnly cookie. The chip is
    // a cosmetic tell that displays "admin mode" only for the tab in
    // which the admin has recently authenticated. We use a boolean
    // non-secret sessionStorage marker `stockaily_admin_hint` written
    // by OwnerConsole on successful TOTP completion.
    return !!window.sessionStorage.getItem("stockaily_admin_hint");
  } catch {
    return false;
  }
}

/**
 * Resolve the conceptual data source the user is looking at right now.
 * Returns one of: "cache" | "fmp" | "mock"
 */
function resolveSource({ cacheHit, liveDataStatus, dataSource }) {
  if (cacheHit) return "cache";
  if (liveDataStatus === "live" || dataSource === "fmp") return "fmp";
  return "mock";
}

export default function LiveDataChip({ data, className = "" }) {
  if (!data) return null;

  const cacheHit = !!data.cache_hit;
  const cachedAt = data.cached_at || null;
  const liveStatus = data.live_data_status || "mock";
  const dataSource = data.data_source || "mock";
  const source = resolveSource({ cacheHit, liveDataStatus: liveStatus, dataSource });

  const admin = isAdminMode();

  // --- Normal user view: silent fallback for mock; subtle pill otherwise ----
  if (!admin) {
    if (source === "mock") return null;

    if (source === "cache") {
      // Phase 7.UI.7 — Cache freshness is an internal concept. Normal
      // users should NOT see "Cached · updated Xm ago" surfaced as a
      // UI chip; it confuses people and exposes infra detail. Admin
      // mode still shows the full cache diagnostic chip below.
      return null;
    }

    // Live FMP
    return (
      <span
        data-testid="live-data-chip"
        data-mode="live"
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${className}`}
        style={{
          background: "rgba(49,130,206,0.12)",
          borderColor: "rgba(49,130,206,0.40)",
          color: "#7BB6F5",
        }}
      >
        <span className="relative inline-flex w-2 h-2" aria-hidden>
          <span
            className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping"
            style={{ background: "#3182CE" }}
          />
          <span
            className="relative inline-flex w-2 h-2 rounded-full"
            style={{ background: "#3182CE" }}
          />
        </span>
        Live data
      </span>
    );
  }

  // --- Admin / Dev mode: detailed chip with tap-to-open tooltip -------------
  const adminLabel = `${source.toUpperCase()} · ${cacheHit ? "cache hit" : "cache miss"}${cacheHit ? ` · ${formatRelative(cachedAt)}` : ""}`;
  const adminTone = source === "fmp"
    ? { bg: "rgba(49,130,206,0.14)", border: "rgba(49,130,206,0.45)", fg: "#7BB6F5", icon: <Wifi className="w-3 h-3" /> }
    : source === "cache"
      ? { bg: "rgba(120,120,140,0.10)", border: "rgba(120,120,140,0.30)", fg: "var(--app-text-secondary)", icon: <Database className="w-3 h-3" /> }
      : { bg: "rgba(229,179,72,0.10)", border: "rgba(229,179,72,0.40)", fg: "#E5B348", icon: <Sparkles className="w-3 h-3" /> };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid="live-data-chip-admin"
          data-mode={source}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium font-mono tracking-tight hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-glow-blue)] ${className}`}
          style={{ background: adminTone.bg, borderColor: adminTone.border, color: adminTone.fg }}
          aria-label="Data source diagnostics"
        >
          <Radio className="w-3 h-3" aria-hidden />
          {adminLabel}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="text-[11px] leading-relaxed">
        <div className="font-semibold mb-1 text-foreground">Data source diagnostics</div>
        <div className="space-y-0.5 font-mono tabular text-[11px]">
          <Row k="data_source"      v={source} />
          <Row k="live_data_status" v={liveStatus} />
          <Row k="cache_hit"        v={String(cacheHit)} />
          <Row k="cache_age"        v={cacheHit ? formatRelative(cachedAt) : "—"} />
          <Row k="cached_at"        v={cachedAt || "—"} />
          <Row k="raw_source"       v={dataSource} />
        </div>
        <div className="mt-2 pt-1.5 border-t border-border/40 text-[10px] text-muted-foreground">
          Visible because <span className="font-mono">stockaily_admin_hint</span> is set.
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground/90">{v}</span>
    </div>
  );
}
